import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer
} from 'recharts';

const pastelColors = [
  '#7FB3D5', '#F1948A', '#82E0AA'
];

// Custom tooltip component to show imported and updated counts
const CustomTooltip = ({ active, payload, label, displayMode }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-gray-800 border border-gray-600 p-3 rounded shadow-lg">
      <p className="text-blue-300 font-medium mb-2">{label}</p>
      {payload.map((entry, index) => {
        if (!entry || !entry.dataKey) return null;
        
        // Extract source name
        const source = entry.dataKey;
        // Get the raw data point for this period and source
        const dataPoint = entry.payload?.[source + '_raw'];
        
        // Check if we have detailed import data
        const hasDetailedData = typeof dataPoint === 'object' && dataPoint !== null;
        const importedCount = hasDetailedData ? dataPoint.imported || 0 : (typeof dataPoint === 'number' ? dataPoint : 0);
        const updatedCount = hasDetailedData ? dataPoint.updated || 0 : 0;
        
        return (
          <div key={`item-${index}`} className="flex flex-col mb-2 last:mb-0">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3" style={{ backgroundColor: entry.fill }}></div>
              <span className="text-white font-medium">{source}:</span>
              <span className="text-white font-bold">{entry.value || 0}</span>
            </div>
            {hasDetailedData && (
              <div className="pl-5 text-xs text-gray-300 mt-1 flex gap-2">
                {(displayMode === 'all' || displayMode === 'imported') && importedCount > 0 && (
                  <span className="text-green-300">{importedCount} new</span>
                )}
                {(displayMode === 'all' || displayMode === 'updated') && updatedCount > 0 && (
                  <span className="text-blue-300">{updatedCount} updated</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function LeadImportChart({ data = [], visibleSources = [], dateRange, displayMode = 'all' }) {
  // Guard against null data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">No data available for this selection.</p>
      </div>
    );
  }

  let filtered = data;

  if (dateRange) {
    const { start, end } = dateRange;
    filtered = data.filter(d => {
      const ts = new Date(d.period);
      return (!start || ts >= new Date(start)) &&
             (!end   || ts <= new Date(end));
    });
  }

  // Guard against empty filtered data
  if (filtered.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">No data available for the selected date range.</p>
      </div>
    );
  }

  const sources = Array.from(
    new Set(
      filtered.flatMap(item =>
        Object.keys(item || {}).filter(key => key !== 'period')
      )
    )
  ).filter(source => visibleSources.length === 0 || visibleSources.includes(source));

  // Process data to handle detailed import counts based on displayMode
  const processedData = filtered.map(item => {
    const processed = { period: item.period };
    
    if (item && typeof item === 'object') {
      Object.entries(item).forEach(([key, value]) => {
        if (key !== 'period') {
          // Store the raw value for tooltip access
          processed[key + '_raw'] = value;
          
          // If value is an object with imported/updated, extract based on displayMode
          if (typeof value === 'object' && value !== null && 
              ('imported' in value || 'updated' in value)) {
            if (displayMode === 'all') {
              processed[key] = (value.imported || 0) + (value.updated || 0);
            } else if (displayMode === 'imported') {
              processed[key] = value.imported || 0;
            } else if (displayMode === 'updated') {
              processed[key] = value.updated || 0;
            }
          } else {
            // Legacy data - for backward compatibility
            if (displayMode === 'all' || displayMode === 'imported') {
              processed[key] = value || 0;
            } else {
              processed[key] = 0; // No updated data in legacy format
            }
          }
        }
      });
    }
    
    return processed;
  });

  // Make sure that only sources with data in the current displayMode are shown
  const sourcesWithData = visibleSources.filter(source => {
    // Check if this source has any non-zero values in the current display mode
    return processedData.some(item => {
      const value = item?.[source];
      return value !== undefined && value > 0;
    });
  });
  
  // Guard against empty sourcesWithData
  if (sourcesWithData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">No data to display for the current filter settings.</p>
      </div>
    );
  }
  
  return (
   <ResponsiveContainer width="100%" height="100%">
  <BarChart
    data={processedData}
    layout="horizontal"
    margin={{ top: 10, right: 10, left: 10, bottom: 60 }}
  >
    <CartesianGrid strokeDasharray="3 3" stroke="#2f2f2f" />
    <XAxis
      dataKey="period"
      interval={0}
      height={60}
      tick={{
        fill: '#d1d5db',
        fontSize: 12,
        angle: -45,
        textAnchor: 'end'
      }}
      axisLine={{ stroke: '#4b5563' }}
    />
    <YAxis
      tick={{ fill: '#d1d5db', fontSize: 12 }}
      axisLine={{ stroke: '#4b5563' }}
    />
    <Tooltip
      content={<CustomTooltip displayMode={displayMode} />}
      cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
    />
    <Legend
      wrapperStyle={{
        paddingTop: 10,
        fontSize: '0.75rem',
        color: '#e5e7eb'
      }}
    />
    {sourcesWithData.map((source, i) => (
      <Bar
        key={source}
        dataKey={source}
        stackId="a"
        fill={pastelColors[i % pastelColors.length]}
        radius={[4, 4, 4, 4]}
      />
    ))}
  </BarChart>
</ResponsiveContainer>
  );
}
